import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  addDemoPlanDay,
  copyDemoPlanDay,
  createDemoAppointment,
  createDemoClient,
  createDemoInvoice,
  createDemoPlanDetail,
  finalizeDemoPlan,
  getDemoMealProgram,
  getDemoPlanDetail,
  getDemoProfile,
  getDemoState,
  listDemoPlanDetails,
  touchDemoPlan,
} from './mockData';
import { isDemoModeEnabled } from './config';
import { DEFAULT_AI_MEAL_PLAN_COHORT_PAYLOAD } from '@/constants/ai-model-options';

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const parseUrl = (config: InternalAxiosRequestConfig) => {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://demo.local';
  const url = new URL(config.url || '/', base);
  const params = config.params as Record<string, unknown> | undefined;
  if (params && typeof params === 'object') {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }
  }
  return url;
};

const ok = (
  config: InternalAxiosRequestConfig,
  data: unknown,
  status = 200,
): AxiosResponse => ({
  data,
  status,
  statusText: status >= 400 ? 'Error' : 'OK',
  headers: {},
  config,
});

const getBody = (config: InternalAxiosRequestConfig): Record<string, unknown> => {
  if (!config.data) return {};
  if (typeof config.data === 'string') {
    try {
      return JSON.parse(config.data) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return config.data as Record<string, unknown>;
};

const paginate = <T,>(items: T[], page = 1, limit = 10) => {
  const safeLimit = Math.max(1, limit);
  const safePage = Math.max(1, page);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / safeLimit));
  const start = (safePage - 1) * safeLimit;
  return {
    items: items.slice(start, start + safeLimit),
    pagination: { page: safePage, limit: safeLimit, total, totalPages, pages: totalPages },
  };
};

const HIDDEN_FROM_DEFAULT_CLIENT_STATUSES = new Set(['draft', 'pending_deletion']);

const getClientCounts = () => {
  const clients = getDemoState().clients;
  return {
    all: clients.filter((client) => !HIDDEN_FROM_DEFAULT_CLIENT_STATUSES.has(client.status)).length,
    active: clients.filter((client) => client.status === 'active').length,
    suspended: clients.filter((client) => client.status === 'suspended' || client.status === 'inactive').length,
    drafts: clients.filter((client) => client.status === 'draft').length,
    pendingDeletion: clients.filter((client) => client.status === 'pending_deletion').length,
  };
};

const isCustomQuestionnaire = (questionnaire: Record<string, unknown>) =>
  questionnaire.id === 'demo-custom-glucose' || questionnaire.type === 'custom';

const isConditionalQuestionnaire = (questionnaire: Record<string, unknown>) =>
  questionnaire.type === 'conditional';

const isCoreQuestionnaire = (questionnaire: Record<string, unknown>) =>
  !isCustomQuestionnaire(questionnaire) && !isConditionalQuestionnaire(questionnaire);

// The full question bank is heavy, so it stays out of the main bundle and only
// loads once a demo session actually opens a questionnaire.
const loadQuestionBank = () =>
  import('./coreQuestionnaireQuestions').then((module) => module.DEMO_QUESTION_BANK);

const buildQuestionnaireListResponse = (items: Record<string, unknown>[]) => ({
  questionnaires: items,
  sections: items,
  items,
  data: { questionnaires: items, sections: items, items },
  total: items.length,
  activeClientsCount: getClientCounts().active,
  responsesThisWeek: 8,
  pendingResponsesCount: 4,
});

const getFilteredClients = (url: URL) => {
  const status = url.searchParams.get('status') || 'all';
  const search = (url.searchParams.get('search') || '').toLowerCase();
  return getDemoState().clients.filter((client) => {
    const matchesStatus =
      status === 'pending_deletion'
        ? client.status === 'pending_deletion'
        : status === 'all' || !status
          ? !HIDDEN_FROM_DEFAULT_CLIENT_STATUSES.has(client.status)
          : client.status === status ||
            (status === 'suspended' && client.status === 'inactive');
    const matchesSearch =
      !search ||
      client.fullName.toLowerCase().includes(search) ||
      client.email.toLowerCase().includes(search);
    return matchesStatus && matchesSearch;
  });
};

const dashboardStats = () => {
  const { appointments } = getDemoState();
  return {
    today: { totalAppointments: Math.min(appointments.length, 2) },
    thisWeek: {
      inPersonSessions: appointments.filter((apt) => apt.appointmentType === 'offline').length,
      scheduledTotal: appointments.length,
      onlineSessions: appointments.filter((apt) => apt.appointmentType !== 'offline').length,
    },
  };
};

const handleDemoRequest = async (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
  const url = parseUrl(config);
  const path = url.pathname;
  const method = String(config.method || 'get').toLowerCase();
  const state = getDemoState();

  if (path === '/auth/subscription') {
    return ok(config, {
      subscription: {
        allowed: true,
        reason: null,
        planCode: 'demo-pro',
        status: 'active',
        startsAt: '2026-01-01T00:00:00.000Z',
        endsAt: '2027-01-01T00:00:00.000Z',
        enforcementEnabled: true,
      },
      enforcementEnabled: true,
    });
  }

  if (path === '/auth/founder-agreement') {
    return ok(config, {
      founderAgreement: {
        allowed: true,
        accessBlocked: false,
        required: false,
        isFounder: false,
        reason: 'not_founder',
        agreementVersion: 'demo',
        acceptedAt: null,
        acceptedVersion: null,
        cohort: null,
        planCode: 'demo-pro',
      },
    });
  }

  if (path === '/auth/ai-meal-plan-options' && method === 'get') {
    return ok(config, {
      data: DEFAULT_AI_MEAL_PLAN_COHORT_PAYLOAD,
    });
  }

  if (path === '/nutritionists/profile' && method === 'get') {
    return ok(config, { success: true, data: getDemoProfile() });
  }

  if (path === '/nutritionists/profile' && (method === 'put' || method === 'patch')) {
    Object.assign(state.user, getBody(config), { updatedAt: new Date().toISOString() });
    return ok(config, { success: true, data: state.user });
  }

  if (path === '/nutritionists/profile-pref') {
    return ok(config, { menuTutorial: true, currency: 'GBP', country: 'GB' });
  }

  if (path === '/notifications/summary') {
    return ok(config, { data: { unreadCount: state.notifications.filter((item) => !item.isRead).length, byPriority: { low: 0, medium: 1, high: 0, urgent: 0 } } });
  }

  if (path === '/notifications') {
    return ok(config, {
      data: {
        items: state.notifications,
        unreadCount: state.notifications.filter((item) => !item.isRead).length,
        pagination: { page: 1, limit: 10, total: state.notifications.length, pages: 1 },
      },
    });
  }

  if (path === '/dashboard/today') {
    const nextAppointment = state.appointments[0] ?? null;
    return ok(config, {
      date: new Date().toISOString().slice(0, 10),
      timezone: 'Europe/London',
      generatedAt: new Date().toISOString(),
      cards: {
        appointmentsToday: { count: state.appointments.length, nextAppointment },
        mealPlansToAction: { count: 1, expiringCount: 1, expiredCount: 0 },
        invoicesAndBilling: { count: state.invoices.filter((invoice) => invoice.status !== 'paid').length, overdueCount: 0, dueCount: 1 },
      },
      appointments: { count: state.appointments.length, items: state.appointments },
      mealPlansToAction: {
        counts: { expiring: 1, expired: 0 },
        expiring: [{
          id: 'demo-meal-plan-1',
          clientId: 'demo-client-1',
          clientName: 'Amelia Hart',
          planName: 'Mediterranean Glucose Support',
          type: 'expiring',
          status: 'draft',
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 2 * 86400000).toISOString(),
          recommendedAction: 'Review and finalize',
        }],
        expired: [],
        items: [],
      },
      invoicesAndBilling: {
        counts: { overdue: 0, due: 1 },
        items: state.invoices.filter((invoice) => invoice.status !== 'paid'),
      },
    });
  }

  if (path === '/dashboard/overview-analytics') {
    return ok(config, {
      date: new Date().toISOString().slice(0, 10),
      timezone: 'Europe/London',
      generatedAt: new Date().toISOString(),
      period: { monthKey: '2026-07', previousMonthKey: '2026-06', monthStart: '2026-07-01', monthEnd: '2026-07-31' },
      cards: {
        totalClients: { count: state.clients.length, newThisMonth: 2, deltaNewClients: 1, trendPoints: [] },
        totalAppointments: { count: state.appointments.length, deltaThisMonth: 3 },
        monthlyRevenue: { amount: 1260, previousMonthAmount: 980, deltaPercent: 28, currency: 'GBP', trendPoints: [] },
        invoicesDue: { count: 1, urgentCount: 0, pendingAmount: 95 },
      },
      recentPayments: { count: 1, items: state.invoices.filter((invoice) => invoice.status === 'paid'), totalCollected: 180 },
      revenue: { amount: 1260, previousMonthAmount: 980, deltaPercent: 28, chart: [], collected: 180, pending: 95, paymentIntervalDaysAvg: 4, paidInvoiceCount: 1 },
      clientActivity: { active: getClientCounts().active, newThisMonth: 2, flags: 1, conditions: [{ name: 'Type 2 diabetes', count: 1, percent: 33 }] },
      questionnaireCompletion: { items: [{ questionnaireId: 'demo-core-intake', name: 'New Client Health Profile', total: 10, completed: 8, percent: 80 }] },
    });
  }

  if (path === '/clients/dashboard') {
    const page = Number(url.searchParams.get('page') || 1);
    const limit = Number(url.searchParams.get('limit') || 10);
    const { items, pagination } = paginate(getFilteredClients(url), page, limit);
    return ok(config, { counts: getClientCounts(), clients: items, pagination });
  }

  if (path === '/clients' && method === 'get') {
    const page = Number(url.searchParams.get('page') || 1);
    const limit = Number(url.searchParams.get('limit') || 10);
    const { items, pagination } = paginate(getFilteredClients(url), page, limit);
    return ok(config, { clients: items, pagination });
  }

  if (path === '/clients' && method === 'post') {
    const client = createDemoClient(getBody(config));
    return ok(config, { client, message: 'Client created in demo mode' }, 201);
  }

  const clientOverviewMatch = path.match(/^\/clients\/([^/]+)\/overview$/);
  if (clientOverviewMatch && method === 'get') {
    const client =
      state.clients.find((entry) => entry.id === decodeURIComponent(clientOverviewMatch[1])) ?? state.clients[0];
    const now = Date.now();
    const nextAppointment = state.appointments
      .filter((apt) => apt.clientId === client.id && new Date(apt.startTime).getTime() > now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0];
    return ok(config, {
      clientId: client.id,
      nextAppointment: nextAppointment
        ? {
            id: nextAppointment.id,
            title: nextAppointment.title,
            startTime: nextAppointment.startTime,
            endTime: nextAppointment.endTime,
            timezone: 'Europe/London',
            status: nextAppointment.status,
            appointmentType: nextAppointment.appointmentType,
            meetingLink: nextAppointment.meetingLink,
            location: nextAppointment.location,
          }
        : null,
      questionnaires: { summary: { totalShared: 3, completed: 2, inProgress: 1 }, items: [] },
      goals: {
        clientGoals: (() => {
          const calories = Number(client.targetCalories) || 1900;
          const grams = (share: number, perGram: number) => Math.round((calories * share) / perGram);
          return {
            calories: { value: calories, unit: 'kcal' },
            protein: {
              grams: { value: grams(0.25, 4), unit: 'g' },
              kcal: { value: Math.round(calories * 0.25), unit: 'kcal' },
            },
            carbohydrates: {
              grams: { value: grams(0.45, 4), unit: 'g' },
              kcal: { value: Math.round(calories * 0.45), unit: 'kcal' },
              energyPercent: { value: 45, unit: '%' },
            },
            totalFat: {
              grams: { value: grams(0.3, 9), unit: 'g' },
              kcal: { value: Math.round(calories * 0.3), unit: 'kcal' },
            },
            saturatedFat: {
              grams: { value: grams(0.1, 9), unit: 'g' },
              kcal: { value: Math.round(calories * 0.1), unit: 'kcal' },
            },
            dietaryFibre: { value: 30, unit: 'g' },
            water: { value: 2.4, unit: 'L' },
          };
        })(),
        weightGoal: 'lose_weight',
        healthGoals: Array.isArray(client.healthGoals) ? client.healthGoals : [],
      },
      medicalConditions: [],
    });
  }

  const clientMatch = path.match(/^\/clients\/([^/]+)$/);
  if (clientMatch && method === 'get') {
    const client = state.clients.find((entry) => entry.id === decodeURIComponent(clientMatch[1])) ?? state.clients[0];
    return ok(config, { client, activeSubscription: null });
  }

  if (clientMatch && method === 'delete') {
    const clientId = decodeURIComponent(clientMatch[1]);
    const index = state.clients.findIndex((entry) => entry.id === clientId);
    if (index === -1) {
      return ok(config, { message: 'Client not found' }, 404);
    }
    state.clients.splice(index, 1);
    return ok(config, { message: 'Client deleted successfully' });
  }

  if (clientMatch && method === 'put') {
    const client = state.clients.find((entry) => entry.id === decodeURIComponent(clientMatch[1]));
    if (client) Object.assign(client, getBody(config), { updatedAt: new Date().toISOString() });
    return ok(config, { client: client ?? state.clients[0] });
  }

  if (path === '/appointments/dashboard') {
    const page = Number(url.searchParams.get('page') || 1);
    const limit = Number(url.searchParams.get('limit') || 20);
    const { items, pagination } = paginate(state.appointments, page, limit);
    return ok(config, {
      appointments: items,
      count: state.appointments.length,
      stats: dashboardStats(),
      tabCounts: { all: state.appointments.length, upcoming: state.appointments.length, past: 0 },
      pagination,
    });
  }

  if (path === '/appointments' && method === 'get') {
    const page = Number(url.searchParams.get('page') || 1);
    const limit = Number(url.searchParams.get('limit') || 100);
    const { items, pagination } = paginate(state.appointments, page, limit);
    return ok(config, { appointments: items, count: state.appointments.length, pagination });
  }

  if (path === '/appointments' && method === 'post') {
    const appointment = createDemoAppointment(getBody(config));
    return ok(config, { appointment }, 201);
  }

  if (path === '/appointments/available-slots' || path === '/appointments/check-availability') {
    const dateParam = url.searchParams.get('date') || new Date().toISOString().slice(0, 10);
    const duration = Number(url.searchParams.get('duration') || 45);
    const timezone = url.searchParams.get('timezone') || 'Europe/London';
    const toHHmm = (value: Date) =>
      `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
    const makeSlot = (hour: number, minute: number) => {
      const start = new Date(`${dateParam}T00:00:00`);
      start.setHours(hour, minute, 0, 0);
      const end = new Date(start.getTime() + duration * 60_000);
      return {
        startTime: toHHmm(start),
        endTime: toHHmm(end),
        startTimeUtc: start.toISOString(),
        endTimeUtc: end.toISOString(),
      };
    };
    return ok(config, {
      date: dateParam,
      duration,
      timezone,
      scheduleTimezone: timezone,
      available: true,
      availableSlots: [makeSlot(9, 30), makeSlot(10, 30), makeSlot(11, 30), makeSlot(14, 0), makeSlot(15, 30), makeSlot(16, 30)],
    });
  }

  if (path === '/availability/schedule') {
    return ok(config, {
      timezone: 'Europe/London',
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map((day) => ({
        day,
        isEnabled: true,
        slots: [{ startTime: '09:00', endTime: '17:00', startTimeUtc: '09:00', endTimeUtc: '17:00' }],
      })),
    });
  }

  if (path === '/availability/breaks' || path === '/availability/time-off') {
    return ok(config, []);
  }

  if (path === '/client-invoices' && method === 'get') {
    const page = Number(url.searchParams.get('page') || 1);
    const limit = Number(url.searchParams.get('limit') || 10);
    const { items, pagination } = paginate(state.invoices, page, limit);
    return ok(config, { data: { invoices: items, items, pagination, summary: { totalDue: 95, totalPaid: 180, currency: 'GBP' } } });
  }

  if (path === '/client-invoices' && method === 'post') {
    const invoice = createDemoInvoice(getBody(config));
    return ok(config, { data: invoice }, 201);
  }

  if (path === '/nutritionists/invoice-context') {
    return ok(config, {
      data: {
        nutritionist: state.user,
        clients: state.clients,
        currency: 'GBP',
        nutritionistCountry: 'GB',
        nutritionistCurrency: 'GBP',
        paymentDueDays: 14,
        vatRate: 20,
        showLogoOnInvoice: true,
        plans: [
          { id: 'demo-plan-initial', name: 'Initial consultation (60 min)', totalAmount: 95 },
          { id: 'demo-plan-follow-up', name: 'Follow-up consultation (45 min)', totalAmount: 65 },
          { id: 'demo-plan-package', name: '3-session support package', totalAmount: 240 },
          { id: 'demo-plan-meal-plan', name: 'Personalized meal plan', totalAmount: 180 },
        ],
      },
    });
  }

  if (path === '/client-invoices/generate-id') {
    return ok(config, { data: { invoiceNo: `INV-DEMO-${1000 + state.invoices.length + 1}` } });
  }

  if (path === '/questionnaires/summary') {
    const type = url.searchParams.get('type') || 'core';
    const questionnaires = state.questionnaires as Record<string, unknown>[];
    const coreQuestionnaires = questionnaires.filter(isCoreQuestionnaire);
    const conditionalQuestionnaires = questionnaires.filter(isConditionalQuestionnaire);
    const customQuestionnaires = questionnaires.filter(isCustomQuestionnaire);
    const activeClientsCount = getClientCounts().active;
    const totalQuestionnaires = questionnaires.length;

    return ok(config, type === 'custom'
      ? {
          type: 'custom',
          activeClientsCount,
          totalQuestionnaires,
          responsesThisWeek: 3,
          pendingResponsesCount: 2,
          customQuestionnairesCount: customQuestionnaires.length,
          publishedCount: customQuestionnaires.filter((item) => item.status !== 'draft').length,
          assignedCustomCount: customQuestionnaires.filter((item) => item.assigned === true).length,
          draftCount: customQuestionnaires.filter((item) => item.status === 'draft').length,
        }
      : {
          type: 'core',
          activeClientsCount,
          totalQuestionnaires,
          responsesThisWeek: 8,
          pendingResponsesCount: 4,
          coreQuestionnairesCount: coreQuestionnaires.length,
          assignedCoreCount: coreQuestionnaires.filter((item) => item.assigned === true).length,
          conditionalQuestionnairesCount: conditionalQuestionnaires.length,
        });
  }

  if (path === '/questionnaires/responses') {
    return ok(config, {
      clientId: url.searchParams.get('clientId') || 'demo-client-1',
      nutritionistId: 'demo-nutritionist',
      totalInstances: 3,
      completedCount: 2,
      pendingCount: 1,
      inProgressCount: 0,
      questionnaires: [],
    });
  }

  if (path === '/questionnaires/core' && method === 'get') {
    const questionnaires = (state.questionnaires as Record<string, unknown>[]).filter(isCoreQuestionnaire);
    return ok(config, buildQuestionnaireListResponse(questionnaires));
  }

  if (path === '/questionnaires/custom' && method === 'get') {
    const questionnaires = (state.questionnaires as Record<string, unknown>[]).filter(isCustomQuestionnaire);
    return ok(config, buildQuestionnaireListResponse(questionnaires));
  }

  if (path === '/questionnaires/conditional' && method === 'get') {
    const questionnaires = (state.questionnaires as Record<string, unknown>[]).filter(isConditionalQuestionnaire);
    return ok(config, buildQuestionnaireListResponse(questionnaires));
  }

  if (path === '/questionnaires/assigned' && method === 'get') {
    const prefer = url.searchParams.get('prefer') || 'core';
    const questionnaires = (state.questionnaires as Record<string, unknown>[])
      .filter((item) => (prefer === 'custom' ? isCustomQuestionnaire(item) : !isCustomQuestionnaire(item)))
      .filter((item) => item.assigned === true);
    return ok(config, buildQuestionnaireListResponse(questionnaires));
  }

  if (path === '/questionnaires/custom-draft' && method === 'get') {
    const drafts = (state.questionnaires as Record<string, unknown>[])
      .filter(isCustomQuestionnaire)
      .filter((item) => item.status === 'draft');
    return ok(config, buildQuestionnaireListResponse(drafts));
  }

  const questionnaireResponsesMatch = path.match(/^\/questionnaires\/([^/]+)\/responses$/);
  if (questionnaireResponsesMatch && method === 'get') {
    const questionnaireId = decodeURIComponent(questionnaireResponsesMatch[1]);
    const questionnaire = state.questionnaires.find((entry) => entry.id === questionnaireId) ?? state.questionnaires[0];
    const [firstClient, secondClient] = state.clients;
    return ok(config, {
      questionnaireId: questionnaire.id,
      total: 2,
      assignedClientsCount: 2,
      responsesCount: 1,
      estimatedMinutes: questionnaire.estimatedTimeMinutes ?? 10,
      questionsCount: questionnaire.questionsCount ?? 0,
      responses: [
        {
          instanceId: 'demo-questionnaire-instance-completed',
          clientId: firstClient?.id ?? 'demo-client-1',
          firstName: firstClient?.firstName ?? 'Amelia',
          lastName: firstClient?.lastName ?? 'Hart',
          email: firstClient?.email ?? 'amelia@example.com',
          sentAt: new Date(Date.now() - 4 * 86400000).toISOString(),
          submittedOn: new Date(Date.now() - 2 * 86400000).toISOString(),
          status: 'completed',
        },
        {
          instanceId: 'demo-questionnaire-instance-pending',
          clientId: secondClient?.id ?? 'demo-client-2',
          firstName: secondClient?.firstName ?? 'Priya',
          lastName: secondClient?.lastName ?? 'Nair',
          email: secondClient?.email ?? 'priya@example.com',
          sentAt: new Date(Date.now() - 1 * 86400000).toISOString(),
          submittedOn: null,
          status: 'pending',
        },
      ],
    });
  }

  const questionnaireByIdMatch = path.match(/^\/questionnaires\/([^/]+)$/);
  if (questionnaireByIdMatch && method === 'get') {
    const questionnaireId = decodeURIComponent(questionnaireByIdMatch[1]);
    const questionnaire = state.questionnaires.find((entry) => entry.id === questionnaireId) ?? state.questionnaires[0];
    const questionBank = await loadQuestionBank();
    const realQuestions = questionBank[String(questionnaire.id)];
    return ok(config, {
      questionnaire: {
        ...questionnaire,
        _id: questionnaire.id,
        type: typeof questionnaire.type === 'string' ? questionnaire.type : 'core',
        questions: realQuestions ?? [
          {
            _id: `${questionnaire.id}-q1`,
            questionText: 'What is the main goal for this consultation?',
            type: 'text',
            validation: { required: true },
            order: 1,
            isActive: true,
          },
          {
            _id: `${questionnaire.id}-q2`,
            questionText: 'How would you rate current energy levels?',
            type: 'single_select',
            options: [
              { label: 'Low', value: 'low' },
              { label: 'Moderate', value: 'moderate' },
              { label: 'High', value: 'high' },
            ],
            validation: { required: true },
            order: 2,
            isActive: true,
          },
        ],
      },
    });
  }

  if (path.startsWith('/questionnaires')) {
    return ok(config, {
      questionnaires: state.questionnaires,
      sections: state.questionnaires,
      total: state.questionnaires.length,
      activeClientsCount: getClientCounts().active,
      responsesThisWeek: 8,
      pendingResponsesCount: 4,
    });
  }

  if (path === '/recipes/custom' && method === 'get') {
    const page = Number(url.searchParams.get('page') || 1);
    const pageSize = Number(url.searchParams.get('pageSize') || url.searchParams.get('limit') || 12);
    const query = (url.searchParams.get('q') || '').toLowerCase();
    const nutritionistId = url.searchParams.get('nutritionistId');
    const recipes = state.recipes.filter((recipe) => {
      const record = recipe as Record<string, unknown>;
      if (nutritionistId && record.isOwn !== true) return false;
      if (query && !String(record.title || record.name || '').toLowerCase().includes(query)) return false;
      return true;
    });
    const { items, pagination } = paginate(recipes, page, pageSize);
    return ok(config, {
      success: true,
      data: {
        items,
        recipes: items,
        pagination: {
          page: pagination.page,
          pageSize,
          total: pagination.total,
          totalCount: pagination.total,
          totalPages: pagination.totalPages,
        },
      },
      recipes: items,
      items,
      pagination: {
        page: pagination.page,
        pageSize,
        total: pagination.total,
        totalCount: pagination.total,
        totalPages: pagination.totalPages,
      },
    });
  }

  const customRecipeDetailsMatch = path.match(/^\/recipes\/custom\/([^/]+)$/);
  if (customRecipeDetailsMatch && method === 'get') {
    const recipeId = decodeURIComponent(customRecipeDetailsMatch[1]);
    const recipe = state.recipes.find((entry) => entry.id === recipeId) ?? state.recipes[0];
    return ok(config, {
      success: true,
      data: {
        ...recipe,
        id: recipe.id,
        name: recipe.name ?? recipe.title,
        imageUrl: recipe.imageUrl,
        prepMinutes: 10,
        cookMinutes: Math.max(0, Number(recipe.totalTimeMinutes ?? 20) - 10),
        totalMinutes: recipe.totalTimeMinutes ?? 20,
        nutritionServings: recipe.servings ?? 1,
        cuisines: recipe.cuisineTypes ?? [],
        dishTypes: [],
        tags: recipe.dietLabels ?? [],
        ingredientLines: [
          '150 g main protein',
          '200 g vegetables',
          '1 tbsp olive oil',
        ],
        ingredients: [
          { id: 'demo-ingredient-1', name: 'Main protein', amount: 150, servingId: 'g', amountGrams: 150 },
          { id: 'demo-ingredient-2', name: 'Vegetables', amount: 200, servingId: 'g', amountGrams: 200 },
          { id: 'demo-ingredient-3', name: 'Olive oil', amount: 1, servingId: 'tbsp', amountGrams: 14 },
        ],
        nutritionPerServing: {
          ...recipe.nutritionPerServing,
          total_fat: recipe.nutritionPerServing?.total_fat ?? recipe.nutritionPerServing?.fat ?? 0,
          dietary_fibre: recipe.nutritionPerServing?.dietary_fibre ?? recipe.nutritionPerServing?.fiber ?? 0,
        },
        instructions: [
          'Prepare the ingredients.',
          'Cook until the protein is done and vegetables are tender.',
          'Plate and serve.',
        ],
        isPublic: true,
      },
    });
  }

  const copyRecipeMatch = path.match(/^\/recipes\/custom\/([^/]+)\/copy$/);
  if (copyRecipeMatch && method === 'post') {
    const recipeId = decodeURIComponent(copyRecipeMatch[1]);
    const sourceRecipe = state.recipes.find((entry) => entry.id === recipeId) ?? state.recipes[0];
    const copiedRecipe = {
      ...sourceRecipe,
      id: `demo-recipe-copy-${state.recipes.length + 1}`,
      title: `${sourceRecipe.title} Copy`,
      name: `${sourceRecipe.name ?? sourceRecipe.title} Copy`,
      isOwn: true,
      canEdit: true,
    };
    state.recipes = [copiedRecipe, ...state.recipes];
    return ok(config, { success: true, data: copiedRecipe }, 201);
  }

  const deleteRecipeMatch = path.match(/^\/recipes\/custom\/([^/]+)$/);
  if (deleteRecipeMatch && method === 'delete') {
    const recipeId = decodeURIComponent(deleteRecipeMatch[1]);
    state.recipes = state.recipes.filter((entry) => entry.id !== recipeId);
    return ok(config, { success: true, message: 'Recipe deleted' });
  }

  if (path.startsWith('/recipes/custom') && method === 'post') {
    const body = getBody(config);
    const recipe = {
      id: `demo-recipe-${state.recipes.length + 1}`,
      title: String(body.title || body.name || 'Demo Recovery Smoothie'),
      name: String(body.title || body.name || 'Demo Recovery Smoothie'),
      servings: Number(body.servings || 2),
      mealTypes: ['Snack'],
      cuisineTypes: ['British'],
      dietLabels: ['Vegetarian'],
      allergens: [],
      intoleranceTags: [],
      totalTimeMinutes: 10,
      totalCalories: 260,
      totalProtein: 18,
      totalCarbs: 34,
      totalFat: 6,
      isOwn: true,
      canEdit: true,
      isVerified: false,
    };
    state.recipes = [recipe, ...state.recipes];
    return ok(config, { success: true, data: recipe }, 201);
  }

  if (path === '/ingredients/custom/metadata' || path === '/food-categories') {
    return ok(config, { data: { categories: [{ id: 'veg', name: 'Vegetables' }, { id: 'protein', name: 'Protein foods' }] }, categories: [] });
  }

  if (path === '/meal-plans/all' && method === 'get') {
    const clientId = url.searchParams.get('clientId') || '';
    const type = url.searchParams.get('type') || '';
    const status = url.searchParams.get('status') || '';
    const search = (url.searchParams.get('search') || '').toLowerCase();
    const page = Number(url.searchParams.get('page') || 1);
    const pageSize = Number(url.searchParams.get('pageSize') || 20);
    const plans = state.mealPlans.filter((plan) => {
      const record = plan as Record<string, unknown>;
      if (record.planType === 'generic' && type !== 'generic') return false;
      if (clientId && record.clientId !== clientId) return false;
      if (type && record.planType !== type && record.generationType !== type) return false;
      if (status) {
        const normalized = status === 'completed' ? 'finalized' : status;
        if (record.status !== normalized && record.status !== status) return false;
      }
      if (search && !String(record.name || '').toLowerCase().includes(search)) return false;
      return true;
    });
    const { items, pagination } = paginate(plans, page, pageSize);
    return ok(config, {
      success: true,
      data: {
        plans: items,
        pagination: {
          page: pagination.page,
          pageSize,
          totalCount: pagination.total,
          totalPages: pagination.totalPages,
          hasNextPage: pagination.page < pagination.totalPages,
          hasPreviousPage: pagination.page > 1,
        },
        summary: {
          totalPlans: plans.length,
          automatedDraftsCount: plans.filter((plan) => (plan as Record<string, unknown>).generationType === 'ai_assisted' && (plan as Record<string, unknown>).status === 'draft').length,
          manualDraftsCount: plans.filter((plan) => (plan as Record<string, unknown>).generationType === 'manual' && (plan as Record<string, unknown>).status === 'draft').length,
          finalizedCount: plans.filter((plan) => (plan as Record<string, unknown>).status === 'finalized').length,
        },
      },
    });
  }

  const draftByIdMatch = path.match(/^\/meal-plans\/drafts\/([^/]+)$/);
  if (draftByIdMatch && method === 'get') {
    const detail = getDemoPlanDetail(decodeURIComponent(draftByIdMatch[1])) ?? listDemoPlanDetails()[0];
    return ok(config, { success: true, data: detail, ...detail });
  }

  if (path.match(/^\/meal-plans\/drafts\/([^/]+)\/generation-progress$/) && method === 'get') {
    return ok(config, { success: true, data: { status: 'completed', progress: 100, completedDays: 7, totalDays: 7 } });
  }

  if (path === '/meal-plans/drafts' && method === 'get') {
    const drafts = state.mealPlans.filter((plan) => (plan as Record<string, unknown>).status === 'draft');
    return ok(config, {
      success: true,
      data: {
        items: drafts,
        drafts,
        mealPlans: drafts,
        pagination: { page: 1, pageSize: 20, total: drafts.length, totalCount: drafts.length, totalPages: 1 },
      },
    });
  }

  if (path === '/meal-plans/templates' && method === 'get') {
    return ok(config, {
      success: true,
      data: { items: [], templates: [], pagination: { page: 1, total: 0, totalPages: 1 } },
    });
  }

  if (path === '/meal-plans/generic/clinical-tags') {
    const tags = [
      { id: 'diabetes', label: 'Diabetes', name: 'Diabetes' },
      { id: 'heart-health', label: 'Heart health', name: 'Heart health' },
      { id: 'pcos', label: 'PCOS', name: 'PCOS' },
      { id: 'low-fodmap', label: 'Low FODMAP', name: 'Low FODMAP' },
    ];
    return ok(config, { success: true, data: { tags, clinicalTags: tags }, tags, clinicalTags: tags });
  }

  if (path === '/meal-plans/generic' && method === 'get') {
    const genericPlans = state.mealPlans.filter((plan) => (plan as Record<string, unknown>).planType === 'generic');
    return ok(config, {
      success: true,
      data: {
        plans: genericPlans,
        items: genericPlans,
        genericPlans,
        pagination: { page: 1, pageSize: 20, totalCount: genericPlans.length, totalPages: 1 },
      },
      genericPlans,
    });
  }

  if (path === '/meal-plans/generic/create' && method === 'post') {
    const detail = createDemoPlanDetail(getBody(config), 'manual', 'generic');
    return ok(config, { success: true, data: { ...detail, genericPlanId: detail.id, planId: detail.id }, ...detail }, 201);
  }

  const genericPlanMatch = path.match(/^\/meal-plans\/generic\/([^/]+)$/);
  if (genericPlanMatch && method === 'get') {
    const detail = getDemoPlanDetail(decodeURIComponent(genericPlanMatch[1]));
    if (detail) return ok(config, { success: true, data: detail, ...detail });
  }

  if (path === '/meal-plans/generate' && method === 'post') {
    const detail = createDemoPlanDetail(getBody(config), 'ai_generated');
    return ok(config, { success: true, data: detail, ...detail }, 201);
  }

  if (path === '/meal-plans/manual/create' && method === 'post') {
    const detail = createDemoPlanDetail(getBody(config), 'manual');
    return ok(config, { success: true, data: detail, ...detail }, 201);
  }

  const manualDraftMatch = path.match(/^\/meal-plans\/manual\/([^/]+)$/);
  if (manualDraftMatch && method === 'get') {
    const detail = getDemoPlanDetail(decodeURIComponent(manualDraftMatch[1])) ?? listDemoPlanDetails()[0];
    return ok(config, { success: true, data: detail, ...detail });
  }

  const finalizeMatch = path.match(/^\/meal-plans\/manual\/([^/]+)\/finalize$/);
  if (finalizeMatch && method === 'post') {
    const detail = finalizeDemoPlan(decodeURIComponent(finalizeMatch[1]));
    return ok(config, { success: true, message: 'Meal plan finalized', data: detail, ...(detail ?? {}) });
  }

  const copyDayMatch = path.match(/^\/meal-plans\/([^/]+)\/copy-day$/);
  if (copyDayMatch && method === 'post') {
    const body = getBody(config);
    const detail = copyDemoPlanDay(
      decodeURIComponent(copyDayMatch[1]),
      Number(body.sourceDay ?? body.fromDay ?? body.day ?? 1),
      body.targetDay != null || body.toDay != null ? Number(body.targetDay ?? body.toDay) : undefined,
    );
    return ok(config, { success: true, message: 'Day copied', data: detail, ...(detail ?? {}) });
  }

  const addDayMatch = path.match(/^\/meal-plans\/([^/]+)\/(add-day|days)$/);
  if (addDayMatch && method === 'post') {
    const detail = addDemoPlanDay(decodeURIComponent(addDayMatch[1]));
    return ok(config, { success: true, message: 'Day added', data: detail, ...(detail ?? {}) });
  }

  const analysisMatch = path.match(/^\/meal-plans\/([^/]+)\/nutritional-analysis$/);
  if (analysisMatch && method === 'get') {
    const detail = getDemoPlanDetail(decodeURIComponent(analysisMatch[1])) ?? listDemoPlanDetails()[0];
    const nutrition = (detail?.nutrition ?? {}) as Record<string, unknown>;
    const daywise = (detail?.suggestions ?? []).map((day) => ({
      dayNumber: day.day,
      dayName: `Day ${day.day}`,
      date: day.date,
      nutrition,
      macros: nutrition,
      micros: {},
      meals: Object.values(day.meals ?? {}),
    }));
    return ok(config, { success: true, daywise, cumulative: nutrition, bodyWeightKg: 72, data: { daywise, cumulative: nutrition } });
  }

  if (path === '/meal-plans/search' && method === 'get') {
    const query = (url.searchParams.get('q') || '').toLowerCase();
    const results = state.recipes
      .filter((recipe) => !query || String(recipe.title).toLowerCase().includes(query))
      .map((recipe) => ({
        id: recipe.id,
        type: 'recipe',
        source: 'cached',
        provider: 'internal',
        name: recipe.title,
        imageUrl: recipe.imageUrl,
        nutrition: recipe.nutritionPerServing,
        allergens: recipe.allergens ?? [],
        dietLabels: recipe.dietLabels ?? [],
        recipe: {
          readyInMinutes: recipe.totalTimeMinutes,
          servings: recipe.servings,
          cuisines: recipe.cuisineTypes ?? [],
          mealTypes: recipe.mealTypes ?? [],
        },
      }));
    return ok(config, {
      results,
      totalResults: results.length,
      offset: 0,
      limit: results.length,
      sources: { cached: results.length },
      searchFilters: {},
    });
  }

  if (path === '/meal-plans/manual/add-recipe' || path === '/meal-plans/manual/add-recipes' || path === '/meal-plans/manual/add-item') {
    const body = getBody(config);
    const detail = touchDemoPlan(String(body.draftId || body.planId || body.mealPlanId || ''));
    return ok(config, { success: true, message: 'Updated in demo mode', data: detail, ...(detail ?? {}) });
  }

  if (path.startsWith('/meal-plans/manual/')) {
    const body = getBody(config);
    const detail = touchDemoPlan(String(body.draftId || body.planId || body.mealPlanId || '')) ?? listDemoPlanDetails()[0];
    return ok(config, { success: true, message: 'Updated in demo mode', data: detail, ...(detail ?? {}) });
  }

  if (path.startsWith('/meal-plans/generic/')) {
    const body = getBody(config);
    const detail = touchDemoPlan(String(body.genericPlanId || body.planId || '')) ?? listDemoPlanDetails()[0];
    return ok(config, { success: true, message: 'Updated in demo mode', data: detail, ...(detail ?? {}) });
  }

  const planByIdMatch = path.match(/^\/meal-plans\/([^/]+)$/);
  if (planByIdMatch && method === 'get') {
    const detail = getDemoPlanDetail(decodeURIComponent(planByIdMatch[1]));
    if (detail) return ok(config, { success: true, data: detail, ...detail });
  }

  if (path.startsWith('/meal-plans')) {
    return ok(config, {
      success: true,
      data: { items: state.mealPlans, mealPlans: state.mealPlans, pagination: { page: 1, total: state.mealPlans.length, totalPages: 1 } },
      mealPlans: state.mealPlans,
      genericPlans: state.mealPlans,
      clinicalTags: [{ id: 'diabetes', label: 'Diabetes' }, { id: 'heart-health', label: 'Heart health' }],
      id: 'demo-meal-plan-created',
      mealPlanId: 'demo-meal-plan-created',
    });
  }

  const mealProgramMatch = path.match(/^\/clients\/([^/]+)\/meal-programs$/);
  if (mealProgramMatch) {
    if (method === 'get') {
      const program = getDemoMealProgram(decodeURIComponent(mealProgramMatch[1]));
      return ok(config, { success: true, data: program });
    }
    return ok(config, { success: true, message: 'Meal program saved in demo mode', data: getDemoMealProgram(decodeURIComponent(mealProgramMatch[1])) });
  }

  if (path === '/recipe-search-client' && method === 'get') {
    const query = (url.searchParams.get('query') || url.searchParams.get('q') || '').toLowerCase();
    const recipes = state.recipes.filter((recipe) => !query || String(recipe.title).toLowerCase().includes(query));
    return ok(config, {
      success: true,
      results: recipes,
      recipes,
      data: { recipes, results: recipes, totalResults: recipes.length },
      totalResults: recipes.length,
    });
  }

  const recipeDetailsMatch = path.match(/^\/recipes\/([^/]+)\/details$/);
  if (recipeDetailsMatch && method === 'get') {
    const recipe = state.recipes.find((entry) => entry.id === decodeURIComponent(recipeDetailsMatch[1])) ?? state.recipes[0];
    return ok(config, {
      success: true,
      data: {
        ...recipe,
        ingredients: [
          { name: 'Main ingredient', amount: 150, unit: 'g' },
          { name: 'Vegetables', amount: 200, unit: 'g' },
          { name: 'Olive oil', amount: 1, unit: 'tbsp' },
        ],
        instructions: ['Prepare the ingredients.', 'Cook according to the method.', 'Plate and serve.'],
        nutrition: recipe.nutritionPerServing,
      },
      ...recipe,
    });
  }

  if (path === '/ingredients/search' && method === 'get') {
    const query = (url.searchParams.get('query') || '').toLowerCase();
    const pantry = [
      { id: 'demo-ing-1', name: 'Rolled oats', calories: 379, protein: 13, carbs: 68, fat: 6.5, unit: '100g' },
      { id: 'demo-ing-2', name: 'Greek yogurt', calories: 97, protein: 9, carbs: 4, fat: 5, unit: '100g' },
      { id: 'demo-ing-3', name: 'Chicken breast', calories: 165, protein: 31, carbs: 0, fat: 3.6, unit: '100g' },
      { id: 'demo-ing-4', name: 'Brown rice', calories: 112, protein: 2.6, carbs: 24, fat: 0.9, unit: '100g' },
      { id: 'demo-ing-5', name: 'Salmon fillet', calories: 208, protein: 20, carbs: 0, fat: 13, unit: '100g' },
      { id: 'demo-ing-6', name: 'Blueberries', calories: 57, protein: 0.7, carbs: 14, fat: 0.3, unit: '100g' },
    ].filter((item) => !query || item.name.toLowerCase().includes(query));
    return ok(config, { success: true, data: pantry, ingredients: pantry, recipes: [] });
  }

  if (path === '/calculations/formulas') {
    return ok(config, { formulas: [{ id: 'henry_oxford', name: 'Henry Oxford' }] });
  }

  if (path.startsWith('/calculations') || path.startsWith('/drv')) {
    return ok(config, { data: {}, requirements: {}, guidelines: [] });
  }

  return ok(config, { success: true, data: {}, items: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 1 } });
};

export const createDemoAxiosAdapter = (): AxiosAdapter => async (config) => {
  await wait(350);
  return handleDemoRequest(config);
};

export const installDemoFetchMock = () => {
  if (typeof window === 'undefined' || !isDemoModeEnabled()) return;

  const originalFetch = window.fetch.bind(window);
  const cleanApiUrl = import.meta.env.VITE_API_URL?.replace(/[^a-zA-Z0-9:/._-]/g, '') || '';

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const shouldMock =
      (Boolean(cleanApiUrl) && rawUrl.startsWith(cleanApiUrl)) ||
      rawUrl.startsWith('/api') ||
      rawUrl.includes('demo-upload.local') ||
      rawUrl.includes('/upload-url');

    if (!shouldMock) {
      return originalFetch(input, init);
    }

    return new Response(JSON.stringify({ success: true, data: {}, fileUrl: 'https://demo-upload.local/file.jpg' }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  };
};
